import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';
import { tokens } from '../../theme/tokens';
import SaveIcon from '@mui/icons-material/Save';
import CalculateIcon from '@mui/icons-material/Calculate';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import UnitPageShell from '../../components/shared/UnitPageShell';
import { useAuth } from '../../context/AuthContext';
import {
  MaterialType,
  RHO_ALUMINIUM,
  RHO_KUPFER,
  formatDecimal,
  getCapacityTable,
  getTemperatureCorrectionFactor
} from '../../utils/cableTables';

type SnackbarState = { message: string; severity: 'success' | 'info' | 'warning' | 'error' } | null;

type CalculationResult = {
  html: string;
  calculatedCurrent: number;
  requiredCrossSectionLoad: number;
  finalRecommendedCrossSection: number;
  voltageDropActualPercent: number;
};

type SavedCalculation = {
  id: string;
  name: string;
  timestamp: number;
  voltageSystem: string;
  power: number;
  powerUnit: 'kW' | 'kVA';
  voltage: number;
  cosPhi?: number;
  length: number;
  installationType: string;
  ambientTemp: number;
  maxVoltageDropPercent: number;
  conductorMaterial: MaterialType;
  calculatedCurrent: number;
  requiredCrossSectionLoad: number;
  voltageDropActualPercent: number;
  finalRecommendedCrossSection: number;
};

const LOCAL_STORAGE_KEY = 'm99_u5_kabelberechnungen';

const voltageSystems = ['DC', 'AC 1-phasig', 'AC 3-phasig'] as const;

const installationTypes = [
  'A1 - Rohr in wärmegedämmter Wand (Aderltg.)',
  'A2 - Rohr in wärmegedämmter Wand (Kabel)',
  'B1 - Rohr auf/in Wand (Aderltg.)',
  'B2 - Rohr auf/in Wand (Kabel)',
  'C - Auf Wand / Kabelrinne (gelocht)',
  'E - Frei in Luft (Abstand Wand)',
  'F - Einadrig, berührend, frei in Luft',
  'G - Einadrig, Abstand, frei in Luft'
];

const conductorMaterials: MaterialType[] = ['Kupfer', 'Aluminium'];

const infoTopics = [
  {
    title: 'Verlegearten (Referenzmethoden)',
    html: `
      <b>Referenzmethoden der Verlegung nach DIN VDE 0298-4 / IEC 60364-5-52</b><br><br>
      Die Art der Verlegung hat großen Einfluss auf die Wärmeabfuhr und damit auf die Strombelastbarkeit eines Kabels.<br><br>
      <b>A1:</b> Aderleitungen im Elektroinstallationsrohr in einer wärmegedämmten Wand<br>
      <b>A2:</b> Mehradrige Leitung/Kabel im Elektroinstallationsrohr in einer wärmegedämmten Wand<br>
      <b>B1:</b> Aderleitungen im Elektroinstallationsrohr auf einer Wand oder in einem Kanal auf/in der Wand<br>
      <b>B2:</b> Mehradrige Leitung/Kabel im Elektroinstallationsrohr auf einer Wand oder in einem Kanal auf/in der Wand<br>
      <b>C:</b> Ein- oder mehradrige Leitung/Kabel auf einer Wand (mit Abstand) oder in gelochten Kabelrinnen<br>
      <b>E:</b> Mehradrige Leitung/Kabel frei in Luft mit Abstand zur Wand (&gt;0,3 × D)<br>
      <b>F:</b> Einadrige Leitungen/Kabel, berührend, frei in Luft mit Abstand zur Wand (&gt;1 × D)<br>
      <b>G:</b> Einadrige Leitungen/Kabel, mit Abstand, frei in Luft mit Abstand zur Wand (&gt;1 × D)<br><br>
      <i>(Quelle: ABB Kabelhandbuch / DIN VDE 0298-4.)</i>
    `
  },
  {
    title: 'Spannungsfall Grundlagen',
    html: `
      <b>Spannungsfall (Î”U)</b><br><br>
      Der Spannungsfall ist der Spannungsverlust entlang einer Leitung aufgrund ihres Widerstands. Ein zu hoher Spannungsfall kann zu Fehlfunktionen führen.<br><br>
      <b>Maximal zulässiger Spannungsfall (typische Werte):</b><br>
      • <b>3%</b> für Beleuchtungsanlagen.<br>
      • <b>4%</b> für andere elektrische Verbrauchsmittel.<br><br>
      <b>Berechnungsformeln (vereinfachte Darstellungen):</b><br>
      DC: ΔU = (2 · L · I · ρ) / A<br>
      AC 1-phasig: ΔU ≈ (2 · L · I · ρ · cosφ) / A<br>
      AC 3-phasig: ΔU ≈ (√3 · L · I · ρ · cosφ) / A<br><br>
      <b>Legende:</b><br>
      L = Leitungslänge (einfach) [m], I = Strom [A], ρ = spezifischer Widerstand [Ω·mm²/m], A = Leiterquerschnitt [mm²], U<sub>N</sub> = Nennspannung [V], cosφ = Leistungsfaktor
    `
  },
  {
    title: 'Umrechnungsfaktoren (Temperatur)',
    html: `
      <b>Temperaturkorrektur für PVC-isolierte Kabel (70°C max. Leitertemperatur)</b><br><br>
      20°C: Faktor 1,12 · 25°C: 1,06 · 30°C: 1,00 · 35°C: 0,94 · 40°C: 0,87 · 45°C: 0,79 · 50°C: 0,71 · 55°C: 0,61
    `
  },
  {
    title: 'Umrechnungsfaktoren (Häufung)',
    html: `
      <b>Häufungsfaktoren bei gebündelten Leitungen</b><br><br>
      Beispiel (Verlegeart C, einlagig, berührend): 1 Kabel: 1,00 · 2: 0,80 · 3: 0,70 · 4: 0,65 · 5: 0,60 · 6: 0,57 · 9: 0,52
      <br><br>Exakte Faktoren hängen von Anordnung, Verlegeart, Anzahl belasteter Adern und Umgebung ab.
    `
  }
];

const formatDateTime = (timestamp: number) =>
  new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'short',
    timeStyle: 'short'
  }).format(timestamp);

const Page: React.FC = () => {
  const { darkMode } = useTheme();
  const { canViewKabelberechnung, canSaveKabelberechnung } = useAuth();
  const [voltageSystem, setVoltageSystem] = useState<typeof voltageSystems[number]>('DC');
  const [powerUnit, setPowerUnit] = useState<'kW' | 'kVA'>('kW');
  const [installationType, setInstallationType] = useState<string>(installationTypes[0]);
  const [material, setMaterial] = useState<MaterialType>('Kupfer');
  const [infoTopicIndex, setInfoTopicIndex] = useState(0);

  const [inputs, setInputs] = useState({
    power: '',
    voltage: '',
    cosPhi: '',
    length: '',
    ambientTemp: '30',
    maxVoltageDropPercent: '3',
    calculationName: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [resultHtml, setResultHtml] = useState<string>('');
  const [lastResult, setLastResult] = useState<CalculationResult | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>(null);

  const [savedCalculations, setSavedCalculations] = useState<SavedCalculation[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<SavedCalculation | null>(null);
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [savedDialogOpen, setSavedDialogOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as SavedCalculation[];
        if (Array.isArray(parsed)) setSavedCalculations(parsed);
      }
    } catch (error) {
      console.error('Fehler beim Laden gespeicherter Berechnungen', error);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(savedCalculations));
    } catch (error) {
      console.error('Fehler beim Speichern der Berechnungen', error);
    }
  }, [savedCalculations]);

  useEffect(() => {
    if (voltageSystem === 'DC') {
      setPowerUnit('kW');
      setInputs(prev => ({ ...prev, cosPhi: '' }));
    }
  }, [voltageSystem]);

  const isAcSystem = voltageSystem.startsWith('AC');

  const handleInputChange = (field: keyof typeof inputs) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputs(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleInstallationChange = (event: any) => {
    setInstallationType(event.target.value);
  };

  const numberFromInput = (value: string): number | null => {
    if (!value?.trim()) return null;
    const normalized = value.replace(',', '.');
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const validateRequired = useCallback(
    (field: keyof typeof inputs, label: string, allowZero = false): number | null => {
      const raw = inputs[field];
      const parsed = numberFromInput(raw);
      if (parsed === null || (!allowZero && parsed <= 0) || (allowZero && parsed < 0)) {
        setErrors(prev => ({ ...prev, [field]: `${label} darf nicht leer sein oder 0.` }));
        return null;
      }
      setErrors(prev => ({ ...prev, [field]: '' }));
      return parsed;
    },
    [inputs]
  );

  const buildResultHtml = (
    data: CalculationResult,
    cosPhiValue: number | null,
    ambientTempValue: number,
    maxDropPercent: number
  ) => {
    const lines = [
      `<b>Berechneter Strom (I<sub>B</sub>):</b> ${formatDecimal(data.calculatedCurrent)} A`,
      `<b>Mindestquerschnitt (Last, ${installationType}, ${material}, ${formatDecimal(ambientTempValue)}°C, ${
        voltageSystem === 'AC 3-phasig' ? '3' : '2'
      }-adrig):</b> ${formatDecimal(data.requiredCrossSectionLoad)} mm²`,
      `<b>Gewählter Querschnitt (Standard):</b> ${formatDecimal(data.finalRecommendedCrossSection)} mm²`,
      `<b>Spannungsfall (ΔU) mit ${formatDecimal(data.finalRecommendedCrossSection)} mm²:</b> ${formatDecimal(
        data.voltageDropActualPercent
      )} % (max. ${formatDecimal(maxDropPercent)} %)`
    ];
    if (cosPhiValue !== null) {
      lines.splice(1, 0, `<b>Leistungsfaktor (cos Ï†):</b> ${formatDecimal(cosPhiValue)}`);
    }
    lines.push('', `<b>Empfohlener Mindestquerschnitt: ${formatDecimal(data.finalRecommendedCrossSection)} mm²</b>`);
    return lines.filter(Boolean).join('<br>');
  };

  const performCalculation = () => {
    setLastResult(null);
    setResultHtml('');

    const power = validateRequired('power', 'Leistung');
    const voltage = validateRequired('voltage', 'Spannung');
    const length = validateRequired('length', 'Länge');
    const ambientTemp = numberFromInput(inputs.ambientTemp) ?? 30;
    const maxVoltageDropPercent = numberFromInput(inputs.maxVoltageDropPercent) ?? 3;

    let cosPhiValue: number | null = null;
    if (isAcSystem) {
      const cosPhi = numberFromInput(inputs.cosPhi);
      if (!cosPhi || cosPhi <= 0 || cosPhi > 1) {
        setErrors(prev => ({ ...prev, cosPhi: 'cos Ï† muss zwischen 0 und 1 liegen.' }));
      } else {
        cosPhiValue = cosPhi;
      }
    }

    if (!power || !voltage || !length || (isAcSystem && cosPhiValue === null)) {
      setResultHtml('Bitte Eingabefehler korrigieren.');
      return;
    }

    const calculatedCurrent = (() => {
      const basePower = power * 1000;
      if (voltageSystem === 'DC') {
        return basePower / voltage;
      }
      // AC System: Umrechnung auf Wirkleistung wenn in kVA eingegeben
      const effectivePower = powerUnit === 'kVA' ? basePower * (cosPhiValue ?? 1) : basePower;
      if (voltageSystem === 'AC 1-phasig') {
        return effectivePower / (voltage * (cosPhiValue ?? 1));
      }
      // AC 3-phasig
      return effectivePower / (voltage * Math.sqrt(3) * (cosPhiValue ?? 1));
    })();

    const temperatureFactor = getTemperatureCorrectionFactor(ambientTemp);
    const adern = voltageSystem === 'AC 3-phasig' ? 3 : 2;
    const capacityTable = getCapacityTable(material, adern as 2 | 3, installationType);

    if (!capacityTable) {
      setResultHtml(`Keine Belastbarkeitsdaten für ${material} / ${installationType} (${adern}-adrig).`);
      return;
    }

    const sortedTable = [...capacityTable].sort((a, b) => a[0] - b[0]);
    let requiredCrossSectionLoad = -1;
    for (const [crossSection, baseLoad] of sortedTable) {
      if (baseLoad * temperatureFactor >= calculatedCurrent) {
        requiredCrossSectionLoad = crossSection;
        break;
      }
    }

    if (requiredCrossSectionLoad < 0) {
      setResultHtml(
        `Kein passender Querschnitt für Strombelastbarkeit ${formatDecimal(calculatedCurrent)} A gefunden (${material} / ${installationType}).`
      );
      return;
    }

    const rho = material === 'Kupfer' ? RHO_KUPFER : RHO_ALUMINIUM;
    const candidateCrossSections = sortedTable
      .map(([section]) => section)
      .filter(section => section >= requiredCrossSectionLoad);

    if (!candidateCrossSections.length) {
      setResultHtml(
        `Kein Standardquerschnitt ≥ ${formatDecimal(requiredCrossSectionLoad)} mm² verfügbar (${material} / ${installationType}).`
      );
      return;
    }

    const calculateVoltageDrop = (crossSection: number) => {
      const cosPhiFactor = cosPhiValue ?? 1;
      const voltageDropAbsolute = (() => {
        switch (voltageSystem) {
          case 'DC':
            return (2 * length * calculatedCurrent * rho) / crossSection;
          case 'AC 1-phasig':
            return (2 * length * calculatedCurrent * rho * cosPhiFactor) / crossSection;
          case 'AC 3-phasig':
            return (Math.sqrt(3) * length * calculatedCurrent * rho * cosPhiFactor) / crossSection;
          default:
            return 0;
        }
      })();
      // Rückgabe in Prozent, nicht in Volt
      return (voltageDropAbsolute / voltage) * 100;
    };

    let finalCrossSection = -1;
    let finalVoltageDropPercent = Number.POSITIVE_INFINITY;

    for (const candidate of candidateCrossSections) {
      const dropPercent = calculateVoltageDrop(candidate);
      if (dropPercent <= maxVoltageDropPercent) {
        finalCrossSection = candidate;
        finalVoltageDropPercent = dropPercent;
        break;
      }
    }

    if (finalCrossSection < 0) {
      const largest = candidateCrossSections[candidateCrossSections.length - 1];
      const largestDrop = calculateVoltageDrop(largest);
      setResultHtml(
        `Kein Standardquerschnitt erfüllt Spannungsfall ≤ ${formatDecimal(maxVoltageDropPercent)} %. ` +
          `Größter geprüfter Querschnitt: ${formatDecimal(largest)} mm² (ΔU = ${formatDecimal(largestDrop)} %).`
      );
      return;
    }

    const calculation: CalculationResult = {
      html: '',
      calculatedCurrent,
      requiredCrossSectionLoad,
      finalRecommendedCrossSection: finalCrossSection,
      voltageDropActualPercent: finalVoltageDropPercent
    };

    const html = buildResultHtml(calculation, cosPhiValue, ambientTemp, maxVoltageDropPercent);
    calculation.html = html;

    setResultHtml(html);
    setLastResult(calculation);
  };

  const handleSaveCalculation = () => {
    if (!canSaveKabelberechnung) {
      setSnackbar({ message: 'Keine Berechtigung zum Speichern.', severity: 'error' });
      return;
    }
    if (!lastResult) {
      setSnackbar({ message: 'Keine gültige Berechnung zum Speichern vorhanden.', severity: 'warning' });
      return;
    }

    const name = inputs.calculationName.trim();
    if (!name) {
      setErrors(prev => ({ ...prev, calculationName: 'Bitte einen Namen vergeben.' }));
      setSnackbar({ message: 'Bitte einen Namen für die Berechnung eingeben.', severity: 'warning' });
      return;
    }

    const power = numberFromInput(inputs.power) ?? 0;
    const voltage = numberFromInput(inputs.voltage) ?? 0;
    const cosPhiValue = numberFromInput(inputs.cosPhi) ?? undefined;
    const length = numberFromInput(inputs.length) ?? 0;
    const ambientTemp = numberFromInput(inputs.ambientTemp) ?? 30;
    const maxVoltageDropPercent = numberFromInput(inputs.maxVoltageDropPercent) ?? 3;

    const entry: SavedCalculation = {
      id: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}`,
      name,
      timestamp: Date.now(),
      voltageSystem,
      power,
      powerUnit,
      voltage,
      cosPhi: isAcSystem ? cosPhiValue : undefined,
      length,
      installationType,
      ambientTemp,
      maxVoltageDropPercent,
      conductorMaterial: material,
      calculatedCurrent: lastResult.calculatedCurrent,
      requiredCrossSectionLoad: lastResult.requiredCrossSectionLoad,
      voltageDropActualPercent: lastResult.voltageDropActualPercent,
      finalRecommendedCrossSection: lastResult.finalRecommendedCrossSection
    };

    setSavedCalculations(prev => [entry, ...prev]);
    setInputs(prev => ({ ...prev, calculationName: '' }));
    setSnackbar({ message: `Berechnung "${name}" gespeichert.`, severity: 'success' });
  };

  const handleDeleteEntry = (entry: SavedCalculation) => {
    setSavedCalculations(prev => prev.filter(item => item.id !== entry.id));
    setSelectedEntry(null);
    setSnackbar({ message: `Berechnung "${entry.name}" gelöscht.`, severity: 'info' });
  };

  const handleClearAll = () => {
    setSavedCalculations([]);
    setConfirmClearAll(false);
    setSnackbar({ message: 'Alle gespeicherten Berechnungen gelöscht.', severity: 'info' });
  };

  const handleReset = useCallback(() => {
    setInputs({
      power: '',
      voltage: '',
      cosPhi: '',
      length: '',
      ambientTemp: '30',
      maxVoltageDropPercent: '3',
      calculationName: ''
    });
    setErrors({});
    setResultHtml('');
    setLastResult(null);
    setVoltageSystem('DC');
    setPowerUnit('kW');
    setInstallationType(installationTypes[0]);
    setMaterial('Kupfer');
  }, []);

  const handleCopyResult = useCallback(() => {
    if (!resultHtml) return;
    const textVersion = resultHtml.replace(/<br>/g, '\n').replace(/<\/?[^>]+(>|$)/g, '');
    navigator.clipboard.writeText(textVersion).then(() => {
      setSnackbar({ message: 'Ergebnis in Zwischenablage kopiert', severity: 'success' });
    });
  }, [resultHtml]);

  const actionButtons = useMemo(
    () => [
      <Tooltip key="info" title="Hintergrundwissen">
        <IconButton onClick={() => setInfoDialogOpen(true)} size="small">
          <InfoOutlinedIcon />
        </IconButton>
      </Tooltip>,
      <Tooltip key="saved" title="Gespeicherte Berechnungen">
        <IconButton onClick={() => setSavedDialogOpen(true)} size="small">
          <BookmarksIcon />
        </IconButton>
      </Tooltip>,
      <Tooltip key="reset" title="Alle Felder zurücksetzen">
        <IconButton onClick={handleReset} size="small">
          <RestartAltIcon />
        </IconButton>
      </Tooltip>,
      <Tooltip key="copy" title="Ergebnis kopieren">
        <span>
          <IconButton onClick={handleCopyResult} size="small" disabled={!resultHtml}>
            <ContentCopyIcon />
          </IconButton>
        </span>
      </Tooltip>
    ],
    [resultHtml, handleCopyResult, handleReset]
  );

  const savedListContent = useMemo(() => {
    if (!savedCalculations.length) {
      return (
        <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
          <RestoreOutlinedIcon sx={{ fontSize: 42, color: darkMode ? tokens.colors.dark.textSecondary : tokens.colors.light.textSecondary }} />
          <Typography variant="body2" sx={{ color: darkMode ? tokens.colors.dark.textSecondary : tokens.colors.light.textSecondary }}>
            Noch keine Berechnungen gespeichert.
          </Typography>
        </Stack>
      );
    }

    return (
      <List sx={{ maxHeight: 360, overflowY: 'auto' }}>
        {savedCalculations.map(entry => (
          <ListItem disablePadding key={entry.id}>
            <ListItemButton onClick={() => setSelectedEntry(entry)}>
              <ListItemText
                primary={entry.name}
                secondary={`${formatDateTime(entry.timestamp)} • ${entry.voltageSystem}`}
              />
              <IconButton
                edge="end"
                onClick={e => {
                  e.stopPropagation();
                  handleDeleteEntry(entry);
                }}
                aria-label="Löschen"
              >
                <DeleteOutlineIcon />
              </IconButton>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  }, [savedCalculations, darkMode]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    performCalculation();
  };

  return (
    <UnitPageShell title="M99 · Kabelquerschnittsberechnung" actions={actionButtons} showTTS={false}>
      {!canViewKabelberechnung ? (
        <Alert severity="error">Keine Berechtigung fuer die Kabelberechnung.</Alert>
      ) : (
      <Card elevation={2}>
        <CardContent>
          <Box component="form" onSubmit={onSubmit}>
            <Stack spacing={3}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text }}>
                Kabelquerschnitt berechnen
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  select
                  fullWidth
                  label="Spannungssystem"
                  value={voltageSystem}
                  onChange={event => setVoltageSystem(event.target.value as typeof voltageSystems[number])}
                >
                  {voltageSystems.map(system => (
                    <MenuItem value={system} key={system}>
                      {system}
                    </MenuItem>
                  ))}
                </TextField>

                <FormControl component="fieldset">
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text, minWidth: 120 }}>
                      Leistungsangabe
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant={powerUnit === 'kW' ? 'contained' : 'outlined'}
                        color="primary"
                        size="small"
                        onClick={() => setPowerUnit('kW')}
                        disabled={powerUnit === 'kW'}
                      >
                        kW
                      </Button>
                      <Button
                        variant={powerUnit === 'kVA' ? 'contained' : 'outlined'}
                        color="primary"
                        size="small"
                        onClick={() => setPowerUnit('kVA')}
                        disabled={voltageSystem === 'DC' || powerUnit === 'kVA'}
                      >
                        kVA
                      </Button>
                    </Stack>
                  </Stack>
                </FormControl>

                <TextField
                  fullWidth
                  label={`Leistung (${powerUnit})`}
                  value={inputs.power}
                  onChange={handleInputChange('power')}
                  error={Boolean(errors.power)}
                  helperText={errors.power}
                  inputMode="decimal"
                />

                <TextField
                  fullWidth
                  label="Spannung U (V)"
                  value={inputs.voltage}
                  onChange={handleInputChange('voltage')}
                  error={Boolean(errors.voltage)}
                  helperText={errors.voltage}
                  inputMode="decimal"
                />

                {isAcSystem && (
                  <TextField
                    fullWidth
                    label="cos Ï†"
                    value={inputs.cosPhi}
                    onChange={handleInputChange('cosPhi')}
                    error={Boolean(errors.cosPhi)}
                    helperText={errors.cosPhi}
                    inputMode="decimal"
                  />
                )}

                <TextField
                  fullWidth
                  label="Kabellänge L (m)"
                  value={inputs.length}
                  onChange={handleInputChange('length')}
                  error={Boolean(errors.length)}
                  helperText={errors.length}
                  inputMode="decimal"
                />

                <TextField
                  select
                  fullWidth
                  label="Verlegeart"
                  value={installationType}
                  onChange={handleInstallationChange}
                >
                  {installationTypes.map(type => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  fullWidth
                  label="Leitermaterial"
                  value={material}
                  onChange={event => setMaterial(event.target.value as MaterialType)}
                >
                  {conductorMaterials.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  label="Umgebungstemperatur (°C)"
                  value={inputs.ambientTemp}
                  onChange={handleInputChange('ambientTemp')}
                  error={Boolean(errors.ambientTemp)}
                  helperText={errors.ambientTemp}
                  inputMode="decimal"
                />

                <TextField
                  fullWidth
                  label="Max. Spannungsfall (%)"
                  value={inputs.maxVoltageDropPercent}
                  onChange={handleInputChange('maxVoltageDropPercent')}
                  error={Boolean(errors.maxVoltageDropPercent)}
                  helperText={errors.maxVoltageDropPercent}
                  inputMode="decimal"
                />
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CalculateIcon />}
                  type="submit"
                >
                  Berechnen
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveCalculation}
                  disabled={!lastResult || !canSaveKabelberechnung}
                >
                  Speichern
                </Button>
                <TextField
                  label="Name der Berechnung"
                  value={inputs.calculationName}
                  onChange={handleInputChange('calculationName')}
                  error={Boolean(errors.calculationName)}
                  helperText={errors.calculationName}
                  sx={{ flexGrow: 1 }}
                />
              </Stack>

              <Divider />

              <Stack spacing={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text }}>
                  Ergebnis
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${tokens.colors.primary}`,
                    minHeight: 140
                  }}
                >
                  {resultHtml ? (
                    <Typography
                      variant="body2"
                      sx={{ color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text, lineHeight: 1.6 }}
                      component="div"
                      dangerouslySetInnerHTML={{ __html: resultHtml }}
                    />
                  ) : (
                    <Stack spacing={1} alignItems="center" justifyContent="center" sx={{ minHeight: 100 }}>
                      <ArticleOutlinedIcon sx={{ fontSize: 40, color: darkMode ? tokens.colors.dark.textSecondary : tokens.colors.light.textSecondary }} />
                      <Typography variant="body2" sx={{ color: darkMode ? tokens.colors.dark.textSecondary : tokens.colors.light.textSecondary }}>
                        Berechnung starten, um das Ergebnis anzuzeigen.
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Stack>
            </Stack>
          </Box>
        </CardContent>
      </Card>
      )}

      {/* Info-Dialog */}
      <Dialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Hintergrundwissen</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <TextField
              select
              fullWidth
              label="Thema wählen"
              value={infoTopicIndex.toString()}
              onChange={event => setInfoTopicIndex(Number.parseInt(event.target.value, 10))}
            >
              {infoTopics.map((topic, index) => (
                <MenuItem key={topic.title} value={index.toString()}>
                  {topic.title}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ p: 3, borderRadius: 2, border: `1px solid ${tokens.colors.primary}` }}>
              <Typography
                variant="body2"
                sx={{ color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text, lineHeight: 1.65 }}
                component="div"
                dangerouslySetInnerHTML={{ __html: infoTopics[infoTopicIndex].html }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Gespeichert-Dialog */}
      <Dialog open={savedDialogOpen} onClose={() => setSavedDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Gespeicherte Berechnungen</Typography>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteSweepIcon />}
              onClick={() => setConfirmClearAll(true)}
              disabled={!savedCalculations.length}
            >
              Alle löschen
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>{savedListContent}</DialogContent>
        <DialogActions>
          <Button onClick={() => setSavedDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Detail-Dialog */}
      <Dialog open={Boolean(selectedEntry)} onClose={() => setSelectedEntry(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Gespeicherte Berechnung</DialogTitle>
        {selectedEntry && (
          <>
            <DialogContent dividers>
              <DialogContentText component="div">
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text, mb: 1 }}>
                  {selectedEntry.name}
                </Typography>
                <Typography variant="body2" sx={{ color: darkMode ? tokens.colors.dark.textSecondary : tokens.colors.light.textSecondary, mb: 2 }}>
                  {formatDateTime(selectedEntry.timestamp)} · {selectedEntry.voltageSystem}
                </Typography>
                <Typography component="div" variant="body2" sx={{ color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text, lineHeight: 1.6 }}>
                  <div><b>Leistung:</b> {formatDecimal(selectedEntry.power)} {selectedEntry.powerUnit}</div>
                  <div><b>Spannung:</b> {formatDecimal(selectedEntry.voltage)} V</div>
                  {selectedEntry.cosPhi !== undefined && (
                    <div><b>cos Ï†:</b> {formatDecimal(selectedEntry.cosPhi)}</div>
                  )}
                  <div><b>Länge:</b> {formatDecimal(selectedEntry.length)} m</div>
                  <div><b>Verlegeart:</b> {selectedEntry.installationType}</div>
                  <div><b>Material:</b> {selectedEntry.conductorMaterial}</div>
                  <div><b>Umgebung:</b> {formatDecimal(selectedEntry.ambientTemp)} °C</div>
                  <div><b>Max. ΔU:</b> {formatDecimal(selectedEntry.maxVoltageDropPercent)} %</div>
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography component="div" variant="body2" sx={{ color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text, lineHeight: 1.6 }}>
                  <div><b>Berechneter Strom:</b> {formatDecimal(selectedEntry.calculatedCurrent)} A</div>
                  <div><b>Querschnitt (Last):</b> {formatDecimal(selectedEntry.requiredCrossSectionLoad)} mm²</div>
                  <div><b>ΔU real:</b> {formatDecimal(selectedEntry.voltageDropActualPercent)} %</div>
                  <div><b>Empfohlener Mindestquerschnitt:</b> {formatDecimal(selectedEntry.finalRecommendedCrossSection)} mm²</div>
                </Typography>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedEntry(null)}>Schließen</Button>
              <Button color="error" onClick={() => handleDeleteEntry(selectedEntry)}>
                Löschen
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Confirm Clear */}
      <Dialog open={confirmClearAll} onClose={() => setConfirmClearAll(false)}>
        <DialogTitle>Alle Berechnungen löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie wirklich alle gespeicherten Berechnungen entfernen?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClearAll(false)}>Abbrechen</Button>
          <Button color="error" onClick={handleClearAll} startIcon={<DeleteSweepIcon />}>
            Alle löschen
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={3500}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </UnitPageShell>
  );
};

export default Page;
